import {
  getActiveEvaluationIds,
  getActiveRecommenders,
  getEvaluatorTrancheList,
  getProposalOrPlaceholder,
} from "@engine/round/accessors";
import type {
  Proposal,
  Recommender,
  RoundAggregate,
} from "@engine/round/aggregate";
import type { TrancheProfile } from "@lib/tranche";
import {
  getFDVFromTranches,
  getFullnessFromTranches,
  getLDTFromTranches,
} from "@lib/tranche/trancheUtils";
import { sortByMultipleProperties } from "@lib/utils/sortByMultipleProperties";

const sectionIds = ["fdv", "ldt", "fullness"] as const;
type SectionId = (typeof sectionIds)[number];

export type ComparisonsMatrixData = {
  rows: ComparisonsMatrixRow[];
  recommenders: Recommender[];
  sectionIds: typeof sectionIds;
};

export type ComparisonsMatrixRow = {
  proposal: Proposal;
  sections: {
    [sectionId in SectionId]: ComparisonsMatrixRowSection;
  };
};

type ComparisonsMatrixRowSection = {
  type: SectionId;
  recommenderValues: { [recommenderId: string]: number | null };
};

export function calculateComparisonsMatrixData(
  round: RoundAggregate,
): ComparisonsMatrixData {
  const rows: ComparisonsMatrixRow[] = [];
  const recommenders = getActiveRecommenders(round);
  recommenders.sort(
    sortByMultipleProperties("meanRecommenderThreshold", "shortname"),
  );

  const activeProposalIds = getActiveEvaluationIds(recommenders);
  const trancheLists: { [recommenderId: string]: TrancheProfile } = {};
  for (const recommender of recommenders) {
    trancheLists[recommender.id] = getEvaluatorTrancheList(round, recommender);
  }

  for (const proposalId of activeProposalIds) {
    const sections: ComparisonsMatrixRow["sections"] = {
      fdv: { type: "fdv", recommenderValues: {} },
      ldt: { type: "ldt", recommenderValues: {} },
      fullness: { type: "fullness", recommenderValues: {} },
    };

    for (const recommender of recommenders) {
      const evaluation = recommender.evaluations[proposalId];
      if (!evaluation || evaluation.state === "canceled") {
        continue;
      }

      const recommenderTranchList = trancheLists[recommender.id];
      const fdv = getFDVFromTranches(recommenderTranchList, proposalId);
      const ldt = getLDTFromTranches(recommenderTranchList, proposalId);
      const fullness = getFullnessFromTranches(
        recommenderTranchList,
        proposalId,
      );

      sections.fdv.recommenderValues[recommender.id] = fdv;
      sections.ldt.recommenderValues[recommender.id] = ldt;
      sections.fullness.recommenderValues[recommender.id] = fullness;
    }

    const proposal = getProposalOrPlaceholder(round, proposalId);

    rows.push({ proposal, sections });
  }

  rows.sort((a, b) => a.proposal.shortname.localeCompare(b.proposal.shortname));

  return {
    rows,
    recommenders,
    sectionIds,
  };
}
